import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../lib/api';
import { AlertFilter, AuditAlert } from '../types/api';
import AlertFilters from '../components/Alerts/AlertFilters';
import AlertList from '../components/Alerts/AlertList';
import Pagination from '../components/UI/Pagination';

const Alerts: React.FC = () => {
  const [filter, setFilter] = useState<AlertFilter>({});
  const [page, setPage] = useState(1);
  const limit = 20;
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['alerts', filter, page],
    queryFn: () => apiClient.getAlerts(filter, page, limit),
    refetchInterval: 30000, // More frequent updates for alerts
  });

  const resolveAlertMutation = useMutation({
    mutationFn: (alertId: string) => apiClient.resolveAlert(alertId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['alerts'] });
    },
  });

  const resolveAllVisibleMutation = useMutation({
    mutationFn: (alertIds: string[]) => {
      return Promise.all(alertIds.map(id => apiClient.resolveAlert(id)));
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['alerts'] });
      console.log('All visible alerts marked as resolved.');
    },
  });

  const handleFilterChange = (newFilter: AlertFilter) => {
    setFilter(newFilter);
    setPage(1);
  };

  const handleResolveAlert = async (alertId: string) => {
    try {
      await resolveAlertMutation.mutateAsync(alertId);
    } catch (error) {
      console.error('Failed to resolve alert:', error);
    }
  };

  const handleResolveAllVisible = async () => {
    const unresolvedVisibleAlerts = data?.items.filter((alert: AuditAlert) => !alert.resolved) || [];
    if (unresolvedVisibleAlerts.length === 0) {
      console.log("No unresolved alerts to mark.");
      return;
    }

    if (confirm(`Are you sure you want to mark all ${unresolvedVisibleAlerts.length} visible alerts as resolved?`)) {
      try {
        const alertIds = unresolvedVisibleAlerts.map(alert => alert.id);
        await resolveAllVisibleMutation.mutateAsync(alertIds);
      } catch (error) {
        console.error('Failed to resolve all visible alerts:', error);
      }
    }
  };

  const unreadCount = data?.items.filter((alert: any) => !alert.resolved).length || 0;

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="border-b border-gray-200 pb-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Bridge Alerts</h1>
            <p className="text-gray-600 mt-1">Monitor system alerts and security notifications</p>
          </div>
          <div className="flex items-center space-x-4">
            {unreadCount > 0 && (
              <button
                onClick={handleResolveAllVisible}
                disabled={resolveAllVisibleMutation.isPending}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:bg-blue-300"
              >
                {resolveAllVisibleMutation.isPending ? 'Resolving...' : 'Mark all visible as resolved'}
              </button>
            )}
            {unreadCount > 0 && (
              <div className="bg-red-100 text-red-800 px-3 py-1 rounded-full text-sm font-medium">
                {unreadCount} unresolved
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Filters */}
      <AlertFilters 
        filter={filter} 
        onFilterChange={handleFilterChange}
      />

      {/* Alerts List */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-medium text-gray-900">
            Alerts
            {data && (
              <span className="ml-2 text-sm text-gray-500">
                ({data.total} total)
              </span>
            )}
          </h2>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        ) : (
          <>
            <AlertList 
              alerts={data?.items || []}
              onResolve={handleResolveAlert}
              resolving={resolveAlertMutation.isPending}
            />
            
            {data && data.pages > 1 && (
              <div className="px-6 py-4 border-t border-gray-200">
                <Pagination
                  currentPage={page}
                  totalPages={data.pages}
                  onPageChange={setPage}
                />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Alerts; 